import Alpine from "alpinejs"

Alpine.data("articlesGrid", function (config = {}) {
    return {
        // State management
        state: "searching",

        // Pagination
        currentPage: 1,
        totalItems: 0,
        shownItemsCount: 0,

        // Search and filters
        searchTerm: "",
        topicUriPathSegment: "",

        // API management
        isLoading: false,
        searchDebounceTimer: null,
        currentSearchAbortController: null,

        // DOM references
        container: null,
        result: {
            items: [],
        },

        init() {
            this.container = this.$el.querySelector(".c-articles-grid__container")

            // Initialize from config or URL parameters
            this.searchTerm = config.search || this.getUrlParam("q") || ""
            this.topicUriPathSegment = config.topicUriPathSegment || this.getUrlParam("topicUriPathSegment") || ""
            this.totalItems = config.totalItems || 0

            // Always start with searching state since we'll load from JSON API
            this.state = "searching"
            this.result = { items: [] }
            this.shownItemsCount = 0

            // Always load articles from JSON API (MaterialFinder approach)
            this.loadArticles()

            // Watch for search changes with debouncing (same pattern as MaterialFinder)
            this.$watch("searchTerm", () => {
                // Always reset to page 1 when search changes
                this.currentPage = 1

                if (this.searchTerm.length > 0) {
                    this.state = "searching"

                    const debounceSearch = (func, delay) => {
                        return () => {
                            const context = this
                            const args = arguments
                            clearTimeout(this.searchDebounceTimer)
                            this.searchDebounceTimer = setTimeout(() => func.apply(context, args), delay)
                        }
                    }

                    debounceSearch(() => {
                        this.loadArticles()
                        this.updateUrl()
                    }, 500)()
                } else {
                    // When search is cleared, reset to initial state
                    this.loadArticles()
                    this.updateUrl()
                }
            })

            // Listen for SearchFilter events
            this.$el.addEventListener("searchFilter:add-item", (event) => {
                if (event.detail && event.detail.identifier) {
                    if (event.detail.identifier === "reset") {
                        // Handle "Show All" - same as clear
                        this.topicUriPathSegment = ""
                        this.currentPage = 1
                        this.loadArticles()
                        this.updateUrl()
                    } else {
                        // Handle specific topic selection - use uriPathSegment if available, fallback to identifier
                        this.topicUriPathSegment = event.detail.uriPathSegment || event.detail.identifier
                        this.currentPage = 1 // Always reset to page 1 when filter changes
                        this.loadArticles()
                        this.updateUrl()
                    }
                }
            })

            this.$el.addEventListener("searchFilter:clear-items", () => {
                this.topicUriPathSegment = ""
                this.currentPage = 1 // Always reset to page 1 when filter clears
                this.loadArticles()
                this.updateUrl()
            })
        },

        getUrlParam(param) {
            const url = new URL(window.location.href)
            return url.searchParams.get(param)
        },

        updateUrl() {
            const url = new URL(window.location.href)

            if (this.searchTerm.trim()) {
                url.searchParams.set("q", this.searchTerm.trim())
            } else {
                url.searchParams.delete("q")
            }

            if (this.topicUriPathSegment) {
                url.searchParams.set("topicUriPathSegment", this.topicUriPathSegment)
            } else {
                url.searchParams.delete("topicUriPathSegment")
            }

            if (this.currentPage > 1) {
                url.searchParams.set("page", this.currentPage.toString())
            } else {
                url.searchParams.delete("page")
            }

            window.history.replaceState({}, "", url)
        },

        async loadArticles(append = false) {
            try {
                // Cancel previous request if not appending
                if (!append && this.currentSearchAbortController) {
                    this.currentSearchAbortController.abort()
                    this.currentSearchAbortController = null
                }

                // Create new AbortController for this request
                this.currentSearchAbortController = new AbortController()

                this.isLoading = true
                if (!append) {
                    this.state = "searching"
                    this.currentPage = 1
                }

                const params = new URLSearchParams({
                    page: this.currentPage.toString(),
                    perPage: config.itemsPerPage || 8,
                    ...(this.searchTerm.trim() && { q: this.searchTerm.trim() }),
                    ...(this.topicUriPathSegment && { topicUriPathSegment: this.topicUriPathSegment }),
                })

                const response = await fetch(`${config.jsonUri}?${params}`, {
                    method: "GET",
                    signal: this.currentSearchAbortController.signal,
                })

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }

                const jsonResult = await response.json()
                this.renderArticlesFromJson(jsonResult, append)

                // Update pagination info
                this.totalItems = jsonResult.total || 0

                // Update current topic from response if provided
                if (jsonResult.topicUriPathSegment !== undefined) {
                    this.topicUriPathSegment = jsonResult.topicUriPathSegment
                }

                this.state = jsonResult.items && jsonResult.items.length > 0 ? "result" : "empty"
            } catch (error) {
                if (error.name !== "AbortError") {
                    console.error("Error loading articles:", error)
                    this.state = "empty"
                }
            } finally {
                this.isLoading = false
            }
        },

        renderArticlesFromJson(jsonResult, append = false) {
            if (!jsonResult || !jsonResult.items) {
                this.result = { items: [] }
                this.shownItemsCount = 0
                return
            }

            if (append && this.result.items) {
                // Append new articles to existing ones
                this.result.items = [...this.result.items, ...jsonResult.items]
            } else {
                // Replace all articles
                this.result = { items: jsonResult.items }
            }

            this.updateShownItems()
        },

        updateShownItems() {
            // With Alpine.js templates, we track items from the result data
            this.shownItemsCount = this.result.items ? this.result.items.length : 0

            if (this.shownItemsCount > this.totalItems) {
                this.totalItems = this.shownItemsCount
            }
        },

        clearSearch() {
            this.searchTerm = ""
        },

        showMore() {
            if (this.isLoading || this.shownItemsCount >= this.totalItems) {
                return
            }

            this.currentPage += 1
            this.loadArticles(true)
            this.updateUrl()
        },
    }
})

Alpine.data("articlesGridFilter", () => ({
    isStuck: false,
    debounceTimeout: null,
    init() {
        // Use requestAnimationFrame for smoother scrolling
        let ticking = false

        window.addEventListener("scroll", () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    const rect = this.$el.getBoundingClientRect()
                    const shouldBeStuck = rect.top <= 80

                    if (this.isStuck !== shouldBeStuck) {
                        this.isStuck = shouldBeStuck

                        // Clear any pending debounced class change
                        if (this.debounceTimeout) {
                            clearTimeout(this.debounceTimeout)
                        }
                        this.debounceTimeout = setTimeout(() => {
                            if (this.isStuck) {
                                this.$el.classList.add("is-stuck")
                            } else {
                                this.$el.classList.remove("is-stuck")
                            }
                        }, 50)
                    }

                    ticking = false
                })

                ticking = true
            }
        })

        // Initial check
        const rect = this.$el.getBoundingClientRect()
        this.isStuck = rect.top <= 80
        if (this.isStuck) {
            this.$el.classList.add("is-stuck")
        }
    },
}))
